import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { CheckIcon } from '@untitledui-icons/react/line'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/base/buttons'
import { FormInput, FormSwitch, FormCombobox } from '@/components/forms'
import { useCreateCustomer } from '@/hooks/api/useCustomers'
import { useSearchPartners } from '@/hooks/api/usePartners'
import { usePermissions } from '@/hooks/api'

// Validation schema
const customerCreateSchema = z.object({
  name: z.string().min(1, 'app.validation.name_required'),
  lookup_key: z.string().optional(),
  partner_id: z.number().nullable().optional(),
  allowed_to_make_templates: z.boolean(),
})

type CustomerCreateFormValues = z.infer<typeof customerCreateSchema>

export function CustomerCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [partnerSearch, setPartnerSearch] = useState('')
  const { isAdmin } = usePermissions()

  // Fetch partners for autocomplete (admin only)
  const { data: partners = [] } = useSearchPartners(partnerSearch, isAdmin)

  // Mutation
  const createCustomer = useCreateCustomer()

  // Form setup
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CustomerCreateFormValues>({
    resolver: zodResolver(customerCreateSchema),
    defaultValues: {
      name: '',
      lookup_key: '',
      partner_id: null,
      allowed_to_make_templates: false,
    },
  })

  // Partner options for combobox
  const partnerOptions = useMemo(() => {
    return partners.map((p) => ({
      value: p.id,
      label: p.name,
    }))
  }, [partners])

  // Handle form submission
  const onSubmit = async (data: CustomerCreateFormValues) => {
    try {
      await createCustomer.mutateAsync({
        name: data.name,
        lookup_key: data.lookup_key || undefined,
        partner_id: data.partner_id || undefined,
        meta: {
          allowed_to_make_templates: data.allowed_to_make_templates,
        },
      })
      toast.success(t('resources.customers.create_success'))
      navigate('/customers')
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('resources.customers.create_error')
      toast.error(message)
    }
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader title={t('resources.customers.create_title')} subtitle={t('resources.customers.create_subtitle')} />

      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-auto px-6 py-4">
        <div className="mx-auto max-w-2xl space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <FormInput
              control={control}
              name="name"
              label={t('resources.customers.fields.name')}
              placeholder={t('resources.customers.placeholders.name')}
              isRequired
            />

            <FormInput
              control={control}
              name="lookup_key"
              label={t('resources.customers.fields.lookup_key')}
              placeholder={t('resources.customers.placeholders.lookup_key')}
              description={t('resources.customers.descriptions.lookup_key')}
            />
          </div>

          {/* Partner (admin only) */}
          {isAdmin && (
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
              <FormCombobox
                control={control}
                name="partner_id"
                label={t('resources.customers.fields.partner_id')}
                options={partnerOptions}
                placeholder={t('resources.customers.placeholders.partner')}
                onSearchChange={setPartnerSearch}
                isRequired
              />
            </div>
          )}

          {/* Settings */}
          <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
            <FormSwitch
              control={control}
              name="allowed_to_make_templates"
              label={t('resources.customers.fields.allowed_to_make_templates')}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-gray-700">
            <Button color="secondary" type="button" onPress={() => navigate('/customers')}>
              {t('resources.generic.cancel')}
            </Button>
            <Button color="primary" type="submit" isDisabled={isSubmitting} iconLeading={CheckIcon}>
              {isSubmitting ? t('resources.generic.loading') : t('resources.generic.save')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default CustomerCreatePage
